import Chamber from "./chamber";
import {
    OUTLET_MANIFOLD_ID,
} from "./types";
import {
    getLargestManifoldTrunkSeparation,
    hasManifoldBendSeparation,
    hasManifoldEndCapSeparation,
} from "./manifolds";
import { getScaledValue } from "./calculations";
import { StoneBedInitialState } from "../reducers/stone-bed-reducer";
import { ManifoldUtils } from "./manifolds/utils";

/**
 * ADS Chamber Row Group Class.
 */
export default class ChamberRow{
    /**
     * @param {number} x - The chamber X coordinate
     * @param {number} y - The chamber Y coordinate
     * @param {number} height - The chamber height
     * @param {number} width - The chamber row Width
     * @param {number} chamberWidth - The chamber Width
     * @param {number} width
     * @param {number} chamberInstalledOffset - The chamber installed offset(overlap)
     * @param {number} endCapWidth - The chamber's endcap Width
     * @param {number} endcapInstalledOffset - The chamber's endcap installed offset(overlap)
     * @param {number} endCapHeight - The chamber's endcap height
     * @param {number} row - The chamber row position in the matrix
     * @param {boolean | number} offSet - The row feet offset
     * @param {boolean | number} pixelOffSet - The row feet offset
     */
    constructor(
        x = 0,
        y = 0,
        height = 0,
        width = 0,
        chamberWidth = 1,
        chamberInstalledOffset = 1,
        endCapWidth = 1,
        endcapInstalledOffset = 1,
        endCapHeight = 1,
        row = 0,
        offSet = 0,
        pixelOffSet = 0,
    ) {
        this._row = row;
        this._x = x;
        this._y = y;
        this._height = height;
        this._width = width;
        this._chamberWidth = chamberWidth;
        this._chamberInstalledOffset = chamberInstalledOffset;
        this._endCapWidth = endCapWidth;
        this._endcapInstalledOffset = endcapInstalledOffset;
        this._endCapHeight = endCapHeight;
        this._OffSet = offSet;
        this._pixelOffSet = pixelOffSet;

        /* instance chambers with 0 offset (no previous elements) */
        this._chambers = [];
    }

    get x() {
        return this._x;
    }

    set x( value ) {
        this._x = value;
    }

    get y() {
        return this._y;
    }

    set y( value ) {
        this._y = value;
    }

    get height() {
        return this._height;
    }

    set height( value ) {
        this._height = value;
    }

    get width() {
        return this._width;
    }

    set width( value ) {
        this._width = value;
    }

    get endCapWidth() {
        return this._endCapWidth;
    }

    set endCapWidth( value ) {
        this._endCapWidth = value;
    }

    get endcapInstalledOffset() {
        return this._endcapInstalledOffset;
    }

    set endcapInstalledOffset( value ) {
        this._endcapInstalledOffset = value;
    }

    get endCapHeight() {
        return this._endCapHeight;
    }

    set endCapHeight( value ) {
        this._endCapHeight = value;
    }

    get chamberWidth() {
        return this._chamberWidth;
    }

    set chamberWidth( value ) {
        this._chamberWidth = value;
    }

    get chamberInstalledOffset() {
        return this._chamberInstalledOffset;
    }

    set chamberInstalledOffset( value ) {
        this._chamberInstalledOffset = value;
    }

    get row() {
        return this._row;
    }

    set row( value ) {
        this._row = value;
    }

    get offSet() {
        return this._OffSet;
    }

    set offSet( value ) {
        this._OffSet = value;
    }

    get pixelOffSet() {
        return this._pixelOffSet;
    }

    set pixelOffSet( value ) {
        this._pixelOffSet = value;
    }

    get chambers() {
        return this._chambers;
    }

    /**
     * Count the number of inlet and outlet manifolds in this row.
     *
     * @returns {Object}
     */
    getInletOutletCount() {
        /* the inlet/outlet counters*/
        const counters = {
            inlet: 0,
            outlet: 0,
        };
        /* the chamber array */
        const chambers = this._chambers;
        /* search active chambers from top down to the desired one*/
        for (let i = 0; i < chambers.length; i++) {
            const cmb = chambers[i];
            if (!cmb.active) {
                continue;
            }
            let ctrs = cmb.getInletOutletCount();
            /* check if has inlet or outlet */
            counters.inlet += ctrs.inlet;
            counters.outlet += ctrs.outlet;
        }
        /* return the inlet/outlet counters */
        return counters;
    }

    /**
     *
     * @param {number} col The position of the chamber to determine if
     * it is interior to the bed.
     *
     * @returns {boolean}
     */
    isInteriorChamber( col ) {
        /* the booleans indicators for any chamber active up or down of
        the desired one */
        let activeUp = false,
            activeBelow = false;
        /* the chamber array */
        const chambers = this._chambers;
        /* search active chambers from top down to the desired one*/
        for (let i = 0; i < col; i++) {
            const cmb = chambers[i];
            if (cmb.active) {
                activeUp = true;
                break;
            }
        }
        /* search active chambers from desired one to the end of the row*/
        for (let i = col + 1; i < chambers.length; i++) {
            const cmb = chambers[i];
            if (cmb.active) {
                activeBelow = true;
                break;
            }
        }
        /* if there are active chambers up and down of the inspected one,
        it is an interior chamber */
        return activeUp && activeBelow;
    }

    validateTransition( chamberId ) {
        /* get the first active chamber */
        for (let i = 0; i < this._chambers.length; i++) {
            const cmb = this._chambers[i];
            if (
                cmb.active &&
                cmb.accessory &&
                cmb.accessory.exceptions &&
                cmb.accessory.exceptions[chamberId] &&
                !cmb.accessory.exceptions[chamberId].attachable
            ) {
                return cmb.accessory;
            }
        }
        return null;
    }

    getFirstActiveChamber() {
        let firstChamber;
        /* get the first active chamber */
        for (let i = 0; i < this._chambers.length; i++) {
            if (this._chambers[i].active) {
                firstChamber = this._chambers[i];
                break;
            }
        }
        return firstChamber;
    }

    getLastActiveChamber() {
        let lastChamber;
        /* find the last active chamber */
        for (let i = this._chambers.length - 1; i >= 0; i--) {
            if (this._chambers[i].active) {
                lastChamber = this._chambers[i];
                break;
            }
        }
        return lastChamber;
    }

    getActiveChamberByCoordinates( coordinates ) {
        return this._chambers[coordinates.col];
    }

    getBoundsCoordinates() {
        let topRight, topLeft, downRight, downLeft;
        let firstChamber, lastChamber;
        let row = this._row;
        let offset = this._pixelOffSet;
        let hasUpperManifold = false;
        let hasLowerManifold = false;

        /* find the first active chamber */
        firstChamber = this.getFirstActiveChamber();
        /* find the last active chamber */
        lastChamber = this.getLastActiveChamber();

        /* check if it has upper manifold */
        if (
            firstChamber.upperCapActive &&
            firstChamber.upperCapAccessory &&
            firstChamber.upperCapAccessory.id
        ) {
            hasUpperManifold = firstChamber.upperCapAccessory.id
                .toLowerCase()
                .includes("manifold");
        }

        /* check if it has upper manifold */
        if (
            lastChamber.lowerCapActive &&
            lastChamber.lowerCapAccessory &&
            lastChamber.lowerCapAccessory.id
        ) {
            hasLowerManifold = lastChamber.lowerCapAccessory.id
                .toLowerCase()
                .includes("manifold");
        }

        let installedEndCapWidth = this._endCapWidth - this._endcapInstalledOffset;
        let installedChamberWidth =
            this._chamberWidth - this._chamberInstalledOffset;

        /* compute the bounds, multiply by row number to know the correct position */
        topRight = {
            x: firstChamber.x - installedEndCapWidth + offset,
            y: firstChamber.y + row * this._height,
        };
        topLeft = {
            x: firstChamber.x - installedEndCapWidth + offset,
            y: firstChamber.y + this._height + row * this._height,
        };
        downRight = {
            x: lastChamber.x + installedChamberWidth + installedEndCapWidth + offset,
            y: lastChamber.y + row * this._height,
        };
        downLeft = {
            x: lastChamber.x + installedChamberWidth + installedEndCapWidth + offset,
            y: lastChamber.y + this._height + row * this._height,
        };
        /* return the computations */
        return {
            topRight,
            topLeft,
            downRight,
            downLeft,
            hasUpperManifold,
            hasLowerManifold,
        };
    }

    /**
     * Function is responsible for bumping the PERIMETER when there are manifolds.
     * When a manifold is find it will bum the perimeter, in this case code for applicableManifolds in order to stop the process for CULTEC
     * @param chamberRows
     * @param manifoldSeparations
     * @param manifolds
     * @param isUpper
     * @param newManifoldSeparations
     * @param scalePixels
     * @param scaleValue
     * @param sepDistance
     * @param chamberRowSpacing
     * @returns {{downLeft: {x: *, y: *}, topRight: {x: *, y: *}, hasLeftBendSeparation: boolean, hasRightEndCapSeparation: boolean, hasLeftEndCapSeparation: boolean, leftManifoldBendSeparation: number, leftManifoldEndCapSeparation: (number|number), rightManifoldEndCapSeparation: (number|number), hasManifold: boolean, topLeft: {x: *, y: *}, downRight: {x: *, y: *}, manifoldSeparation: *, hasRightBendSeparation: boolean, rightManifoldBendSeparation: number}}
     */
    getBoundsCoordinatesWithManifolds(
        chamberRows,
        manifoldSeparations,
        manifolds,
        isUpper,
        newManifoldSeparations,
        scalePixels,
        scaleValue,
        sepDistance,
        chamberRowSpacing ) {
        let topRight, topLeft, downRight, downLeft;
        let firstChamber, lastChamber;
        let row = this._row;
        let offset = this._pixelOffSet;
        firstChamber = this.getFirstActiveChamber();
        lastChamber = this.getLastActiveChamber();
        const chambersSize = chamberRows[0]._chambers.length;

        // CULTEC MOD to modify the perimeter.
        // Cycle through manifolds - Perimeter Filter for manifolds
        /*let applicableManifolds = [...manifolds]
            .filter(( manifold ) => {
                const isConnected = manifold._assembly
                    .find(assemblyItem => {
                        const increment = manifold._isUpper ? -1 : 1;
                        let col = manifold._letColumn + increment;
                        let nextChamber = chamberRows[this.row].chambers[col];
                        let shouldRun = true;
                        const runFor = manifold._isUpper
                            ? manifold._letColumn - 1
                            : chambersSize - manifold._letColumn - 2;
                        for (let i = 0; i <= runFor; i++) {
                            if (nextChamber.active) {
                                shouldRun = false;
                                break;
                            }
                            col += increment;
                            nextChamber = chamberRows[this.row].chambers[col];
                        }
                        return assemblyItem.rowIndex === this.row && shouldRun;
                    });
                const applyOnlyOnExternal = !manifold._isManifoldInner;
                return isConnected && applyOnlyOnExternal;
            });*/
        let applicableManifolds = [];

        let largestSep = getLargestManifoldTrunkSeparation(
            this,
            chamberRows,
            applicableManifolds,
            manifoldSeparations,
            true,
            sepDistance || 12,
        );

        let manifoldSeparation = getScaledValue(scalePixels, scaleValue, largestSep);

        const manifoldBendSeparation = hasManifoldBendSeparation(this, chamberRows, manifolds, manifoldSeparations);
        const manifoldEndCapSeparation = hasManifoldEndCapSeparation(this, chamberRows, manifolds, manifoldSeparations);

        let stubOD = 0;
        let localTrunkSeparation = 0;
        let localStubSeparation = 0;
        let bendSeparation = 0;
        let endCapSeparation = 0;

        const manifoldPipeSizes = ManifoldUtils.getAllManifoldPipeSizeData();
        let myMfld = applicableManifolds
            .filter(manifold => {
                    return manifold.getTrunkSeparation(
                            this,
                            chamberRows,
                            manifoldSeparations,
                            false,
                            sepDistance || 12)
                        === largestSep;
                },
            )[0];

        if (myMfld) {
            let stubPSize = manifoldPipeSizes.find(
                size => size.pipeSize === myMfld.manifoldSize.stubNominal,
            );

            let trunkPSize = manifoldPipeSizes.find(
                size => size.pipeSize === myMfld.manifoldSize.trunkNominal,
            );

            stubOD = stubPSize.pipeOD;
            localTrunkSeparation = trunkPSize.stonePerimeter;
            localStubSeparation = stubPSize.stonePerimeter;

            if (sepDistance > ( StoneBedInitialState.sepDistance + 0.1 )) {
                const sepDistanceFt = ( sepDistance / 12 );
                if (localTrunkSeparation < sepDistanceFt)
                    manifoldSeparation += getScaledValue(scalePixels, scaleValue, sepDistanceFt - localTrunkSeparation);
            }

            let sep = this._height / 2
                + getScaledValue(scalePixels, scaleValue, ( chamberRowSpacing / 2 ))
                - getScaledValue(scalePixels, scaleValue, ( stubOD / 2 ))
                - getScaledValue(scalePixels, scaleValue, localTrunkSeparation);
            let edcpSep = this._height / 2
                + getScaledValue(scalePixels, scaleValue, ( chamberRowSpacing / 2 ))
                - getScaledValue(scalePixels, scaleValue, ( stubOD / 2 ))
                - getScaledValue(scalePixels, scaleValue, localStubSeparation);
            bendSeparation = trunkPSize.pipeSize === stubPSize.pipeSize ? sep : 0;
            endCapSeparation = trunkPSize.pipeSize !== stubPSize.pipeSize ? edcpSep : 0; //same distance from the stub to the end (perpendicular to the trunk) of the manifold perimeter
        }

        let
            rightManifoldBendSeparation = manifoldBendSeparation.findIndex(item => item === 1) !== -1 ? bendSeparation : 0,
            leftManifoldBendSeparation = manifoldBendSeparation.findIndex(item => item === -1) !== -1 ? bendSeparation : 0;

        const
            rightManifoldEndCapSeparation = manifoldEndCapSeparation.findIndex(item => item === 1) !== -1 ? -endCapSeparation : 0,
            leftManifoldEndCapSeparation = manifoldEndCapSeparation.findIndex(item => item === -1) !== -1 ? -endCapSeparation : 0;

        // Remove bend if at end
        const isLastRow = () => chamberRows.length - 1 === this.row;
        const isFirstRow = () => this.row === 0;

        try {
            if (leftManifoldBendSeparation && !isLastRow()) {
                if (isUpper && chamberRows[this.row + 1].getFirstActiveChamber().col !== firstChamber.col) {
                    leftManifoldBendSeparation = 0;
                } else if (!isUpper && chamberRows[this.row + 1].getLastActiveChamber().col !== lastChamber.col) {
                    leftManifoldBendSeparation = 0;
                }
            }

            if (rightManifoldBendSeparation && !isFirstRow()) {
                if (isUpper && chamberRows[this.row - 1].getFirstActiveChamber().col !== firstChamber.col) {
                    rightManifoldBendSeparation = 0;
                } else if (!isUpper && chamberRows[this.row - 1].getLastActiveChamber().col !== lastChamber.col) {
                    rightManifoldBendSeparation = 0;
                }
            }
        } catch (exception) {
        }

        let installedEndCapWidth = this._endCapWidth - this._endcapInstalledOffset;
        let installedChamberWidth =
            this._chamberWidth - this._chamberInstalledOffset;

        // compute the bounds, multiply by row number to know the correct position
        topRight = {
            x: firstChamber.x - installedEndCapWidth + offset,
            y: firstChamber.y + row * this._height,
        };
        topLeft = {
            x: firstChamber.x - installedEndCapWidth + offset,
            y: firstChamber.y + this._height + row * this._height,
        };
        downRight = {
            x: lastChamber.x + installedChamberWidth + installedEndCapWidth + offset,
            y: lastChamber.y + row * this._height,
        };
        downLeft = {
            x: lastChamber.x + installedChamberWidth + installedEndCapWidth + offset,
            y: lastChamber.y + this._height + row * this._height,
        };

        return {
            topRight,
            topLeft,
            downRight,
            downLeft,
            hasManifold: manifoldSeparation > 0,
            hasRightBendSeparation: rightManifoldBendSeparation !== 0,
            hasLeftBendSeparation: leftManifoldBendSeparation !== 0,
            hasRightEndCapSeparation: rightManifoldEndCapSeparation !== 0,
            hasLeftEndCapSeparation: leftManifoldEndCapSeparation !== 0,
            manifoldSeparation,
            rightManifoldEndCapSeparation,
            leftManifoldEndCapSeparation,
            rightManifoldBendSeparation,
            leftManifoldBendSeparation,
        };
    }

    hasCornerOutlet() {
        let firstChamber, lastChamber;
        let upperAccessory, lowerAccessory;
        firstChamber = this.getFirstActiveChamber();
        lastChamber = this.getLastActiveChamber();
        upperAccessory = firstChamber.upperCapAccessory;
        lowerAccessory = lastChamber.lowerCapAccessory;
        return (
            ( upperAccessory && upperAccessory.id === OUTLET_MANIFOLD_ID ) ||
            ( lowerAccessory && lowerAccessory.id === OUTLET_MANIFOLD_ID )
        );
    }

    hasInternalOutlet() {
        const firstChamberIndex = this.getFirstActiveChamber().col;
        const lastChamberIndex = this.getLastActiveChamber().col;
        return !!( this.chambers.find(c => c.col > firstChamberIndex
            && c.col < lastChamberIndex
            && ( ( c.upperCapAccessory && c.upperCapAccessory.id === OUTLET_MANIFOLD_ID )
                || ( c.lowerCapAccessory && c.lowerCapAccessory.id === OUTLET_MANIFOLD_ID ) )) );
    }

    getActiveCount() {
        return this._chambers.filter(c => c.active).length;
    }

    removeLastChamber() {
        /* inactive last active chamber */
        for (let i = this._chambers.length - 1; i >= 0; i--) {
            let c = this._chambers[i];
            if (c.active) {
                c.active = false;
                c.upperCapActive = c.lowerCapActive = false;
                return;
            }
        }
    }

    disableNeighborChambersEndcaps( idx ) {
        let prevCmbr = this._chambers[idx - 1];
        let nextCmbr = this._chambers[idx + 1];
        if (prevCmbr) prevCmbr.lowerCapActive = false;
        if (nextCmbr) nextCmbr.upperCapActive = false;
    }

    reindex( row ) {
        this._row = row;
        for (let i = 0; i < this._chambers.length; i++) this._chambers[i].row = row;
    }

    /**
     *
     * @param {Object} keyValues The key values to be modified in that chamber.
     * @param {number} idx The index of the chamber.
     */
    modifyChamber( keyValues, idx ) {
        if (!this._chambers[idx]) {
            return;
        }
        let chamber = this._chambers[idx];

        if ( // Do not allow removal of chambers used by a manifold
            chamber._active === true &&
            chamber._isManifoldConnectedArray.length === 0 &&
            keyValues && keyValues.active !== undefined && keyValues.active === false
        ) {
            Object.assign(chamber, keyValues);
        } else if ( // run in any other cases, that is not chamber.active
            chamber._active === false &&
            chamber._isManifoldConnectedArray.length === 0 &&
            keyValues && keyValues.active !== undefined && keyValues.active === true
        ) {
            if ( // check chamber to activate will not be in use of a void
                chamber._active === false &&
                chamber._isVoidArray.length > 0 &&
                keyValues && keyValues.active !== undefined && keyValues.active === true
            ) {
                console.info("Cannot activate chamber, it is in use");
            } else {
                Object.assign(chamber, keyValues);
            }
        } else if (keyValues && keyValues.active && ( keyValues.lowerCapActive || keyValues.upperCapActive )) {
            Object.assign(chamber, keyValues);
        } else if ( // run in any other cases, that is not chamber.active
            keyValues && keyValues.active === undefined
        ) {
            Object.assign(chamber, keyValues);
        } else if ( // do not remove
            chamber._active === true &&
            chamber._isManifoldConnectedArray.length > 0 &&
            keyValues && keyValues.active !== undefined && keyValues.active === false
        ) {
            console.info("Do not remove this chamber, it is in use");
        } else { // all other cases
            Object.assign(chamber, keyValues);
        }


        if (!chamber.active) {
            chamber.upperCapActive = chamber.lowerCapActive = false;
            chamber._upperCapAccessory = chamber._lowerCapAccessory = null;
        } else {
            this.disableNeighborChambersEndcaps(idx);
        }
        return chamber;
    }

    modifyChamberMeta( keyValues, idx ) {
        if (!this._chambers[idx]) {
            return;
        }
        let chamber = this._chambers[idx];
        let chamberMetaData = [];
        chamberMetaData.push(keyValues.chamberMeta);
        Object.assign(chamber, { _chamberMetaData: [...chamber._chamberMetaData, ...chamberMetaData] });
        return chamber;
    }

    modifyChamberIsolatorMeta( keyValues, idx ) {
        if (!this._chambers[idx]) {
            return;
        }
        let chamber = this._chambers[idx];
        let isIsolatorArray = [];
        if (chamber._upperCapActive || chamber._lowerCapActive) {
            const isOutlet = (
                ( chamber._upperCapAccessory !== null && chamber._upperCapAccessory.id === "Outlet-Manifold" ) ||
                ( chamber._lowerCapAccessory !== null && chamber._lowerCapAccessory.id === "Outlet-Manifold" )
            );

            if (chamber._isIsolatorArray.findIndex(x => x === keyValues.isIsolatorArray.manifoldId) === -1 && !isOutlet)
                isIsolatorArray.push(keyValues.isIsolatorArray.manifoldId);
        } else {
            if (chamber._isIsolatorArray.findIndex(x => x === keyValues.isIsolatorArray.manifoldId) === -1)
                isIsolatorArray.push(keyValues.isIsolatorArray.manifoldId);
        }

        Object.assign(chamber, { _isIsolatorArray: [...chamber._isIsolatorArray, ...isIsolatorArray] });
        return chamber;
    }

    modifyChamberVoidMeta( keyValues, idx ) {
        if (!this._chambers[idx]) {
            return;
        }
        let chamber = this._chambers[idx];
        let isVoidArray = [];
        isVoidArray.push(keyValues.isVoidArray.manifoldId);
        if (!chamber._active) {
            Object.assign(chamber, { _isVoidArray: [...chamber._isVoidArray, ...isVoidArray] });
        }
        return chamber;
    }

    modifyChamberManifoldConnectedArrayMeta( keyValues, idx ) {
        if (!this._chambers[idx]) {
            return;
        }
        let chamber = this._chambers[idx];
        let isManifoldConnectedArray = [];

        Object.assign(chamber, keyValues);
        if (chamber._active !== false) {
            isManifoldConnectedArray.push(keyValues.isManifoldConnectedArray.manifoldId);
        }
        Object.assign(chamber, { _isManifoldConnectedArray: [...chamber._isManifoldConnectedArray, ...isManifoldConnectedArray] });
        return chamber;
    }

    modifyChamberManifoldOppositeStubMeta( keyValues, idx ) {
        if (!this._chambers[idx]) {
            return;
        }
        let chamber = this._chambers[idx];
        Object.assign(chamber, { _isOppositeStubAttached: keyValues["isOppositeStubAttached"].value });
        return chamber;
    }

    resetChambersMetadata(
        manifoldId,
        actualCol,
    ) {
        try {
            if (!this._chambers[actualCol]) {
                return;
            }
            let chamber = this._chambers[actualCol];
            let metadataArray = chamber.chamberMetaData;
            let removeIndex = -1;
            removeIndex = metadataArray.findIndex(meta => {
                return meta.manifoldId === manifoldId;
            });
            if (removeIndex !== undefined && removeIndex !== -1) {
                metadataArray.splice(removeIndex, 1);
            }
            Object.assign(chamber, { _chamberMetaData: metadataArray });
            return chamber;
        } catch (exception) {
            console.error(`ChamberRow.resetChambersMetadata() :: ${exception.message}`);
        }
    }

    resetChambersIsolatorArray(
        manifoldId,
        actualCol,
    ) {
        try {
            if (!this._chambers[actualCol]) {
                return;
            }
            let chamber = this._chambers[actualCol];
            let isIsolatorArray = [...chamber.isIsolatorArray];
            let removeIndex = -1;
            removeIndex = isIsolatorArray.findIndex(meta => {
                return meta === manifoldId;
            });
            if (removeIndex !== undefined && removeIndex !== -1) {
                isIsolatorArray.splice(removeIndex, 1);
            }
            Object.assign(chamber, { _isIsolatorArray: isIsolatorArray });
            return chamber;
        } catch (exception) {
            console.error(`ChamberRow.resetChambersMetadata() :: ${exception.message}`);
        }
    }

    resetChambersVoidArray(
        manifoldId,
        actualCol,
    ) {
        try {
            if (!this._chambers[actualCol]) {
                return;
            }
            let chamber = this._chambers[actualCol];
            let isVoidArray = chamber._isVoidArray;
            let removeIndex = -1;
            removeIndex = isVoidArray.findIndex(meta => {
                return meta === manifoldId;
            });

            if (removeIndex !== undefined && removeIndex !== -1) {
                isVoidArray.splice(removeIndex, 1);
            }
            Object.assign(chamber, { _isVoidArray: isVoidArray });
            return chamber;
        } catch (exception) {
            console.error(`ChamberRow.resetChambersVoidArray() :: ${exception.message}`);
        }
    }

    resetChambersManifoldConnectedArray(
        manifoldId,
        actualCol,
    ) {
        try {
            if (!this._chambers[actualCol]) {
                return;
            }
            let chamber = this._chambers[actualCol];
            let isManifoldConnectedArray = chamber.isManifoldConnectedArray;
            let removeIndex = -1;
            removeIndex = isManifoldConnectedArray
                .findIndex(meta => {
                    return meta === manifoldId;
                });
            if (removeIndex !== undefined && removeIndex !== -1) {
                isManifoldConnectedArray.splice(removeIndex, 1);
            }
            Object.assign(chamber, { _isManifoldConnectedArray: isManifoldConnectedArray });
            return chamber;
        } catch (exception) {
            console.error(`ChamberRow.resetChambersManifoldConnectedArray() :: ${exception.message}`);
        }
    }


    autoRemoveEndcaps() {
        /* disable all endcaps */
        this._chambers.forEach(c => {
            c.lowerCapActive = false;
            c.upperCapActive = false;
        });
    }

    /**
     *
     * @param {number} rqCmbrSlotsForTwoEndcaps The required inbetween empty chamber slots to allocate
     * 2 endcaps. For example, one empty slots is not enough to allocate 2 MC-4500 endcaps.
     */
    autoAddEndcaps( rqCmbrSlotsForTwoEndcaps ) {
        /* this object reference */
        let self = this;
        /* the first and last active chambers */
        let firstActiveChamber = null;
        let lastActiveChamber = null;

        /* automatically enable endcaps */
        this._chambers.forEach(( c, i ) => {
            /* previous chamber */
            let prevCmbrs = self._chambers.slice(i - rqCmbrSlotsForTwoEndcaps, i);
            let nextCmbrs = self._chambers.slice(
                i + 1,
                i + 1 + rqCmbrSlotsForTwoEndcaps,
            );
            /* set first active chamber */
            if (!firstActiveChamber && c.active) {
                firstActiveChamber = c;
            }
            /* set last active chamber */
            if (c.active) {
                lastActiveChamber = c;
            }
            /*If upper endcap fits enable, if lower endcap fits, enable.
              This is calculated by checking if there is space up and down
              to fit the endcaps. In some cases, there is the need of more
              than one chamber slot to fit 2 endcaps(the lower endcap from upper chamber
              and the upper endcap from this chamber), I used every to make sure
              there is enough space.
            */
            if (c.active) {
                c.upperCapActive =
                    prevCmbrs.every(ch => !ch.active) &&
                    prevCmbrs.length >= rqCmbrSlotsForTwoEndcaps;
                c.lowerCapActive =
                    nextCmbrs.every(ch => !ch.active) &&
                    nextCmbrs.length >= rqCmbrSlotsForTwoEndcaps;
            }
        });

        /* enabling upper and lower chamber caps by default */
        if (firstActiveChamber) {
            firstActiveChamber.upperCapActive = true;
        }
        if (lastActiveChamber) {
            lastActiveChamber.lowerCapActive = true;
        }
    }

    /**
     *  * Count the number of active endcaps in the row.
     */
    countEndCaps() {
        return this._chambers.reduce(( accu, c ) => {
            return accu + ( c.lowerCapActive ? 1 : 0 ) + ( c.upperCapActive ? 1 : 0 );
        }, 0);
    }

    /**
     *  * Change all chambers to active or inactive.
     * @param {boolean} active
     */
    autoSwitch( active ) {
        this._chambers.forEach(( chamber, idx ) => {
            /* disable or enable endcap */
            chamber.active = active;
            /* if not active, disable endcaps */
            if (!chamber.active) {
                chamber.lowerCapActive = false;
                chamber.upperCapActive = false;

                chamber._upperCapAccessory = null;
                chamber._lowerCapAccessory = null;

            }
            /* if active, remove neighboring chambers endcaps */
            if (chamber.active) {
                this.disableNeighborChambersEndcaps(idx);
            }
        });
    }

    clone() {
        /* make a clone of the object */
        let clone = Object.assign(Object.create(this), this);
        /* clone the chambers */
        clone._chambers = this._chambers.map(c => {
            /* if this chamber is not null, clone */
            if (c) {
                return c.clone();
            } else {
                return c;
            }
        });
        /* return the row clone */
        return clone;
    }

    /* resize all elements dimensions */
    resizeDimensions() {
        /* this row reference */
        let self = this;
        /* get current array lenght*/
        let currentLen = this._chambers.length;
        /* compute new lenght */
        let newLen = this.getNumberOfChambers();
        /* truncate from new lenght if smaller */
        this._chambers.splice(newLen);
        /* in case of new lenght being larger, append more elements */
        if (newLen > currentLen) {
            this._chambers.splice(
                currentLen,
                0,
                ...this.instanceChambers(currentLen, newLen - currentLen),
            );
        }
        /* resize inner chambers */
        this._chambers.forEach(( c, i ) => {
            /* update each chamber width, height, and x position */
            c.width = self._chamberWidth;
            c.height = self._height;
            if (i !== 0) {
                c.x = i * ( c.width - self.chamberInstalledOffset ) + self._endCapWidth;
            } else {
                c.x = i * c.width + self._endCapWidth;
            }
        });
    }

    /**
     * Get the number of chamber that can be allocated in the dimensions of this row.
     */
    getNumberOfChambers() {
        let numOfChambers =
            ( this._width - ( this._endCapWidth - this._endcapInstalledOffset ) * 2 ) /
            ( this._chamberWidth - this._chamberInstalledOffset );

        return Math.floor(Math.round(numOfChambers));
    }

    /**
     * @param {number} offSet the chambers before the current one.
     * @param {number} n the number of chambers to be created.
     */
    instanceChambers( startAt, n ) {
        let self = this;
        /* if number of chambers(n) was not provided, calculate */
        if (!n) {
            n = this.getNumberOfChambers();
        }
        /* create array of size n and return a new array of chambers */
        return new Array(n).fill(0).map(( v, i ) => {
            return new Chamber(// The X coordinate, the index of
                //        previous element + current position by the width of the chamber.
                ( startAt + i ) * ( self._chamberWidth - self._chamberInstalledOffset ) +
                ( self._endCapWidth - self._endcapInstalledOffset ),
                0,
                self._height,
                self._chamberWidth,
                self._row,
                startAt + i,
                false,
                null,
            );
        });
    }
}

export const cloneChamberRows = chamberRows => {
    return chamberRows.slice().map(chamberRow => chamberRow.clone());
};



// WEBPACK FOOTER //
// ./src/lib/chamber-row.js